import {
  BlueBirdTheme,
  Breadcrumb, Button, Divider, Loading, Typography,
} from "@kitbird/core";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { format } from "date-fns";
import { PageCard } from "../../../components/Content/Content.style";
import { ApprovalModalType, PageType } from "./enums";
import {
  byteStringToFileSize, isHtmlString, toRupiah, toStringDate,
} from "../../../utils/common";
import ChecklistLine, { ChecklistDataI } from "../../../components/ChecklistLine";
import { ReactComponent as PdfIcon } from "../../../assets/icons/pdf_file.svg";
import { ReactComponent as ImgIcon } from "../../../assets/icons/img_icon.svg";
import { ReactComponent as ShowIcon } from "../../../assets/icons/show_icon.svg";
import { FileWrapper, FileItem, DescriptionCol } from "./components/styles";
import { IGetDetailTrxResponse, IGetTrxData } from "../../../services/trxService";
import { CommonTrxTypes } from "../../../enums/common";
import { getDocUrl } from "../../../services/bbOneGcsService";
import LabelAndValue from "./components/LabelAndValue";
import useAuth from "../../../components/Authentication/useAuth";
import { getAreas, IAreaData } from "../../../services/areaService";

const {
  black, danger, gray, primary,
} = BlueBirdTheme.colors;

interface SubmissionDetailI {
  pageType: PageType,
  setPageType: React.Dispatch<React.SetStateAction<PageType>>,
  detailRow: IGetTrxData | undefined,
  setDetailRow: React.Dispatch<React.SetStateAction<IGetTrxData | undefined>>,
  detail: IGetDetailTrxResponse | undefined,
  setDetail: React.Dispatch<React.SetStateAction<IGetDetailTrxResponse | undefined>>,
  isApprovalModal: ApprovalModalType | false,
  setApprovalModal: React.Dispatch<React.SetStateAction<ApprovalModalType | false>>,
  isLoading: boolean,
  refetchTable: () => void,
}

const SubmissionDetail = ({
  pageType,
  setPageType,
  detailRow,
  setDetailRow,
  detail,
  setApprovalModal,
  isLoading,
  refetchTable,
}: SubmissionDetailI) => {
  const { userInfo } = useAuth();
  const [allArea, setAllArea] = useState<IAreaData[]>([]);

  const { data: dataAreas } = useQuery({
    queryKey: "get_areas",
    queryFn: getAreas,
  });

  useEffect(() => {
    if (dataAreas) setAllArea(dataAreas.listareaall);
  }, [dataAreas]);

  const generateChecklist: () => ChecklistDataI[] = () => {
    const rows = detail ? [...detail.trx_approvals] : [];
    const approverRows = rows.filter((row) => {
      const isChecklist = row.job_flow === "CHECKLIST";
      const isExecute = row.job_flow === "EXECUTE";
      const isCreator = row.job_flow === "CREATOR";
      const isSecondExtend = detailRow?.extend_bm && detailRow?.extend_bm === "2";
      const isFirstExtend = detailRow?.extend_bm && detailRow?.extend_bm === "1";
      const isRedReceipt = detail?.trx_type?.trx_type === "BMH";

      return (
        !isChecklist
        && !(isExecute && isFirstExtend && isSecondExtend)
        && !(!isCreator && isRedReceipt && isFirstExtend)
      );
    });
    const approvalLine: any[] = approverRows.reverse().map((approval, i, arr) => {
      const isActive = approval.status_approval !== "WAITING" && approval.status_approval !== "OPEN";
      const isCreated = approval.status_approval === "CREATED";
      const isApproved = approval.status_approval === "APPROVED" && approval.job_flow === "APPROVER";
      const isRejected = approval.status_approval === "REJECTED"
        && (approval.job_flow === "APPROVER" || approval.job_flow === "EXECUTE");
      const isExecuted = approval.status_approval === "APPROVED" && approval.job_flow === "EXECUTE";

      const jobFlowLabel = (val: number) => ({
        CREATOR: "Pemohon",
        APPROVER: `Approval level ${val}`,
        EXECUTE: "Transfer dana",
      })[approval.job_flow];

      const approvalDescription = ([
        { desc: "Dibuat : ", condition: isCreated },
        { desc: "Disetujui : ", condition: isApproved },
        { desc: "Ditolak : ", condition: isRejected },
        { desc: "Diproses : ", condition: isExecuted },
      ]).find((item) => item.condition)?.desc;

      return (
        {
          key: i,
          label: (
            <Typography
              level={6}
              fontWeight={isActive ? "medium" : "regular"}
              color={isActive ? black : gray.disable}
            >
              {jobFlowLabel(arr.length - i - 1)}
            </Typography>
          ),
          description: (<div>
            <Typography
              level={6}
              color={isActive ? black : gray.disable}
            >
              {approval.employee_name}
            </Typography>
            <br />
            {isActive
              && <Typography
                level={7}
                color={isRejected ? danger[500] : primary[500]}
                fontWeight="bold"
              >
                {approvalDescription}
                {toStringDate(approval.approval_end, true, true)}
              </Typography>
            }
            {(isRejected || isApproved)
              && <>
                <br />
                <Typography
                  level={7}
                  color={isRejected ? danger[300] : primary[300]}
                  fontWeight="medium"
                >
                  {approval.reason_status}
                </Typography>
              </>}
          </div>),
          active: isActive,
          checkType: isRejected ? "cross" : "check",
        }
      );
    });

    const isPaymentSuccess = detail?.transfer_confirmation?.status === "Succeed";
    const transferDate = detail?.transfer_confirmation?.confirmation_date
      && new Date(detail?.transfer_confirmation?.confirmation_date);
    if (transferDate) transferDate.setHours(transferDate.getHours() - 7);

    const approvalComplete = {
      key: "approval-complete",
      label: <Typography
        level={6}
        fontWeight={isPaymentSuccess ? "medium" : "regular"}
        color={isPaymentSuccess ? black : gray.disable}
      >Transfer selesai</Typography>,
      description: <Typography
        level={7}
        color={primary[500]}
        fontWeight="medium"
      >
        {isPaymentSuccess && "Selesai : "}
        {isPaymentSuccess && transferDate && toStringDate(format(transferDate, "yyyy-MM-dd HH:mm:ss"), true, true)}
      </Typography>,
      active: isPaymentSuccess,
      checkType: "check",
    };
    const isRedReceiptIjir = detail?.trx_type?.trx_type === "BKI" || detail?.trx_type?.trx_type === "BKS";

    return isRedReceiptIjir
      || (detailRow?.extend_bm && detailRow?.extend_bm !== "0")
      ? approvalLine
      : [approvalComplete, ...approvalLine];
  };

  const isCurrentApprover = () => {
    const currentApproval = detail && detail.trx_approvals
      .find((item) => item.employee_email === userInfo.email);
    return (
      detail?.status_trx === "OPEN"
      && pageType === PageType.DETAIL
      && currentApproval?.status_approval === "WAITING"
      && currentApproval?.job_flow !== "EXECUTE"
    );
  };

  return (detail
    ? <>
      <Breadcrumb
        items={[
          pageType === PageType.HISTORY_DETAIL
            ? { title: "Riwayat pengajuan bon", path: "HISTORY_LIST" }
            : { title: "Pengajuan bon", path: "LIST" },
          { title: "Detail pengajuan" },
        ]}
        onClick={(item) => {
          refetchTable();
          if (item.path && item.path === "LIST") {
            setPageType(PageType.LIST);
            setDetailRow(undefined);
          } else if (item.path && item.path === "HISTORY_LIST") {
            setPageType(PageType.HISTORY_LIST);
            setDetailRow(undefined);
          }
        }}
      />
      <div style={{
        display: "flex", gap: 16, paddingBottom: 16, height: "100%",
      }}>
        <PageCard style={{
          width: 280, padding: 16, display: "flex", flexDirection: "column", gap: 16, height: "fit-content", minHeight: "calc(100% - 32px)",
        }}>
          <Typography level={6} color={gray.title} fontWeight="medium">Status</Typography>
          <ChecklistLine data={generateChecklist()} />
        </PageCard>
        <PageCard style={{ padding: 16, height: "fit-content", minHeight: "calc(100% - 32px)" }}>
          {detail
            && <div style={{ display: "flex", gap: 24, flexDirection: "column" }}>
              <div style={{ display: "grid", gridTemplateColumns: "30% auto", gap: 16 }}>
                <Typography level={6} color={gray.title} fontWeight="medium">
                  Detail pemohon
                </Typography>
                <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", rowGap: 16 }}>
                  <LabelAndValue label="Nama & NIP" value={<>{detail.employee_name} • <Typography level={6} fontWeight="bold" color={primary[400]}>{detail.employee_nip}</Typography></>} />
                  <LabelAndValue label="Wilayah" value={allArea.find((area) => area.id === detail.area_id)?.name} />
                  <LabelAndValue label="Pool" value={detail.pool_id} />
                  <LabelAndValue label="Segmen" value={detail.segment?.code_description} />
                  <LabelAndValue label="Departemen" value={detail.department?.department} />
                  <LabelAndValue label="Jabatan" value={detail.trx_approvals[0]?.job_title} />
                  <LabelAndValue label="Kode perusahaan" value={detail.company_code} />
                  <LabelAndValue label="Perusahaan relasi" value={detail.company_relation || "-"} />
                </div>
              </div>
              <Divider color={gray.line} />
              <div style={{ display: "grid", gridTemplateColumns: "30% auto", gap: 16 }}>
                <Typography level={6} color={gray.title} fontWeight="medium">
                  Detail pengajuan
                </Typography>
                <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", rowGap: 16 }}>
                  <LabelAndValue label="No. pengajuan bon" value={detail.trx_id} />
                  <LabelAndValue label="Tipe transaksi" value={detail?.trx_type?.description} />
                  {detail?.trx_type?.trx_type === "BMH" && detail.detail_bon_merah?.bon_merah_type
                    ? <LabelAndValue label="Tipe bon merah" value={detail.detail_bon_merah?.bon_merah_type} />
                    : <></>
                  }
                  <LabelAndValue label="Tanggal pengajuan" value={toStringDate(detail.trx_date)} />
                  <LabelAndValue label="Total bon" value={toRupiah(detail.amount)} />
                  <LabelAndValue label="Akun GL" value={<>
                    {detail.gnlldr}
                    &nbsp;•&nbsp;
                    {CommonTrxTypes.find(
                      (type) => type.value === detail.sap_shkzg,
                    )?.label}
                    <br />
                    {detail.trx_desc}
                  </>} />
                  <LabelAndValue label="Rekening tujuan" value={`${detail.bank_account} • ${detail.bank_account_name}`} />
                  <LabelAndValue
                    label="Deskripsi"
                    value={isHtmlString(detail.trx_text)
                      ? (<DescriptionCol
                        dangerouslySetInnerHTML={{ __html: detail.trx_text }}
                      />)
                      : (<DescriptionCol>
                        {detail.trx_text}
                      </DescriptionCol>)
                    } />
                </div>
              </div>
              <Divider color={gray.line} />
              <div style={{ display: "grid", gridTemplateColumns: "30% auto", gap: 16 }}>
                <Typography level={6} color={gray.title} fontWeight="medium">
                  Lampiran
                </Typography>
                <FileWrapper>
                  {detail.trx_files.map((doc, i) => (
                    <FileItem key={`${doc.id}-${i}`} onClick={async () => window.open(await getDocUrl(doc))}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {doc.file_name.split(".").pop() === "pdf" ? <PdfIcon /> : <ImgIcon />}
                      </div>
                      <div className="ellipsis-text">
                        {doc.file_name}
                        <div>
                          <Typography level={7} fontWeight="medium">{byteStringToFileSize(doc.file_size)}</Typography>
                        </div>
                      </div>
                      <div style={{ height: 21.6, display: "flex", alignItems: "center" }}>
                        <ShowIcon style={{ color: gray.disable }} />
                      </div>
                    </FileItem>
                  ))}
                </FileWrapper>
              </div>
              <Divider color={gray.line} />
              <div style={{ display: "flex", gap: 16, justifyContent: "end" }}>
                <Button
                  variant="text"
                  onClick={() => {
                    refetchTable();
                    setPageType(window.location.pathname.includes("riwayat-pengajuan-bon") ? PageType.HISTORY_LIST : PageType.LIST);
                    setDetailRow(undefined);
                  }}
                  disabled={isLoading}
                >
                  Kembali
                </Button>
                {isCurrentApprover()
                  && <>
                    <Button
                      backgroundColor={danger[500]}
                      onClick={() => setApprovalModal(ApprovalModalType.DETAIL_REJECT)}
                      disabled={isLoading}
                    >Tolak</Button>
                    <Button variant="primary"
                      onClick={() => setApprovalModal(ApprovalModalType.DETAIL_APPROVE)}
                      disabled={isLoading}
                    >Setujui</Button>
                  </>
                }
              </div>
            </div>
          }
          {isLoading && <Loading variant="circular" size={48} />}
        </PageCard>
      </div>
    </>
    : <Loading variant="circular" />
  );
};

export default SubmissionDetail;
